'use client'

import React, { useEffect, useRef } from 'react'
import styles from './styles.module.css'
import { useUiStore } from '../../../../store/uiStore'
import Text from '../../../Text'
import CtaV2 from '../../../CtaV2'
import { toPath } from '../../../../utils/urls'
import SmartLink from '../../../SmartLink'

const cx = (...c) => c.filter(Boolean).join(' ')

// `slug` is the current page's region-less slug, threaded as a prop from the
// section dispatch (RenderSections on the client path + RenderSectionsServer on
// the RSC island path) — sourced from the App-Router route (page.js params),
// NOT from Overmind. It selects the active nav item below.
function SecondaryNavbar({ title, navItems, navItemsComponents, ctaV2, slug = null, sx = {
  titleTextType: 'button-1',
  hideTitleOnMobile: false,
  largeContainer: false
} }) {
  const headerRef = useRef(null)

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleScroll = (event) => {
    const scrollTop = event.target.scrollingElement.scrollTop
    // navbarElementProps now lives on the Zustand ui store (Phase C2 part 1). Read
    // it imperatively in the handler (not a subscribed selector) so this component
    // does not re-render on every scroll tick, exactly as the Overmind proxy did
    // for a field only read inside a handler.
    const { offsetTop: mainNavbarTop, offsetHeight: mainNavbarHeight } =
      useUiStore.getState().navbarElementProps

    if (scrollTop > 0) {
      // if scroll has occurred
      let mainNavbarToScrollDiff = mainNavbarTop - scrollTop
      headerRef.current.style.top = Number(mainNavbarHeight +
        (Number(mainNavbarToScrollDiff) >= 0 ? Number(mainNavbarToScrollDiff) : 0)
      ) + 'px'
    } else {
      headerRef.current.style.top = Number(mainNavbarTop + mainNavbarHeight) + 'px'
    }
  }

  return (
    <div ref={headerRef} className={cx(styles.wrapper, sx.hideTitleOnMobile && styles.wrapperHideTitleOnMobile)}>
      <div className={cx(styles.contentWrapper, sx.largeContainer && styles.contentWrapperLarge)}>
        <div className={cx(styles.titleWrapper, sx.hideTitleOnMobile && styles.titleWrapperHideTitleOnMobile)}>
          {/* Render a product-name "G2" in VRS red. highlight runs AFTER price-token
              replacement, so it never touches the "G2" inside ${..._G2_PRICE}. No-op
              for titles without "G2" (e.g. the R295 navbar). */}
          <Text type={sx.titleTextType} highlight="G2" highlightSx="color: #E11D48;" highlightExactMatch>{title}</Text>
          <div className={styles.ctaDesktop}><CtaV2 {...ctaV2} sx={{ ...ctaV2.sx, height: '32px' }}/></div>
        </div>
        <div className={cx(styles.navigationAndCta, sx.hideTitleOnMobile && styles.navigationAndCtaHideTitleOnMobile)}>
          <div className={styles.navItems}>
            { navItems && navItems.map((i, idx) => renderNavItem(i, slug, idx)) }
            { navItemsComponents && navItemsComponents.map((i, idx) => (
              <React.Fragment key={idx}>{i}</React.Fragment>
            ))}
          </div>
          <div className={cx(styles.ctaMobile, sx.hideTitleOnMobile && styles.ctaMobileHideTitleOnMobile)}>
            <CtaV2 {...ctaV2} sx={{ ...ctaV2.sx, height: '32px' }}/>
          </div>
        </div>
      </div>
    </div>
  )
}

const renderNavItem = (navItem, currentSlug, keyId) => {
  const route = navItem.routeOrUrl && navItem.routeOrUrl.route
  return (
    // The list key must be on the OUTERMOST mapped element — the <SmartLink> —
    // so React does not warn ("Each child in a list should have a unique key";
    // #127). Key by the nav item name (stable), falling back to the map index.
    <SmartLink
      href={toPath(navItem.routeOrUrl)}
      key={navItem.name || keyId}
      className={cx(styles.navItem, (route.slug && route.slug.current === currentSlug) && styles.navItemSelected)}
    >
      <Text type="small">{navItem.name}</Text>
    </SmartLink>
  )
}

export default SecondaryNavbar
